import { useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { KB_TAG_OPTIONS, KB_TAG_SLUGS, useKnowledgeTags } from '@/features/knowledge_base';
import { useMembers } from '@/features/members';

import { DIRECTION_LABELS } from '../constants';
import { useCreateAgentInstall, useUpdateAgentInstall } from '../queries';
import { RECOMMENDED_KB_TAGS, RECOMMENDED_TOOLS } from '../recommendations';
import { TOOL_OPTIONS } from '../toolCatalog';
import type {
  AgentInstall,
  AgentInstallInput,
  AgentInstallStatus,
  AgentTemplate,
} from '../types';

const STATUSES: AgentInstallStatus[] = ['draft', 'active', 'paused'];
// Access scope is org-wide in v1 — the field is hidden in the form (the model +
// API keep it for a future per-role/department/member rollout).

const uniq = (arr: string[]): string[] => [...new Set(arr)];

/** One-click "prefill with recommended" — a subtle sparkles action by a field. */
function RecommendButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      title="Prefill with recommended"
      className="text-muted-foreground hover:text-foreground h-auto gap-1 px-2 py-0.5 text-xs"
      onClick={onClick}
    >
      <Sparkles className="size-3.5" /> Recommended
    </Button>
  );
}

/**
 * Install a template or edit an existing install. Exactly one of `template`
 * (install mode) or `install` (edit mode) is provided. The parent remounts this
 * via a `key` when the target changes, so state initializes from props directly
 * (mirrors KnowledgeBaseDialog / CollectionDialog — no reset effect).
 */
export function InstallFormDialog({
  template,
  install,
  open,
  onOpenChange,
}: {
  template: AgentTemplate | null;
  install: AgentInstall | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const create = useCreateAgentInstall();
  const update = useUpdateAgentInstall();

  // KB-tag suggestions: the canonical catalog (grouped) + any tags actually in
  // use in this org's KB or already selected on the install (grouped "In use"),
  // deduped against the catalog. Creatable still allows brand-new tags.
  const kbTagsQuery = useKnowledgeTags(open);
  const kbTagOptions: MultiSelectOption[] = useMemo(() => {
    const inCatalog = new Set(KB_TAG_SLUGS);
    const extra = [...new Set([...(kbTagsQuery.data ?? []), ...(install?.kb_tags ?? [])])]
      .filter((t) => !inCatalog.has(t))
      .sort()
      .map((value) => ({ value, group: 'In use' }));
    return [...KB_TAG_OPTIONS, ...extra];
  }, [kbTagsQuery.data, install?.kb_tags]);

  const [enabled, setEnabled] = useState(install?.enabled ?? true);
  const [status, setStatus] = useState<AgentInstallStatus>(install?.status ?? 'active');
  const [allowedTools, setAllowedTools] = useState<string[]>(install?.allowed_tools ?? []);
  const [kbTags, setKbTags] = useState<string[]>(install?.kb_tags ?? []);
  // Visibility: public (org-wide) or private (only chosen members).
  const [visibility, setVisibility] = useState<'public' | 'private'>(
    install?.access_scope === 'members' ? 'private' : 'public',
  );
  const [members, setMembers] = useState<string[]>(() =>
    (install?.access_ids ?? []).map(String),
  );

  // Member options for the private-access picker (fetched when open).
  const membersQuery = useMembers({ is_active: true, page_size: 500 });
  const memberOptions: MultiSelectOption[] = useMemo(
    () =>
      (membersQuery.data?.results ?? []).map((m) => ({
        value: String(m.id),
        label: m.user.full_name,
      })),
    [membersQuery.data],
  );
  // Newest-first list of template versions available to pin. Empty in edit mode
  // (no template prop) — the current pin is still preserved via `pinnedVersion`.
  const versions = template?.versions ?? [];
  // "latest" leaves pinned_version null (track the newest version); a numeric
  // value pins that specific version id.
  const [pinnedVersion, setPinnedVersion] = useState<string>(
    install?.pinned_version != null ? String(install.pinned_version) : 'latest',
  );

  const name = install?.template_name ?? template?.name ?? '';
  const direction = install?.direction ?? template?.direction;

  const submit = () => {
    const base: AgentInstallInput = {
      enabled,
      status,
      allowed_tools: allowedTools,
      kb_tags: kbTags,
      access_scope: visibility === 'private' ? 'members' : 'org_wide',
      access_ids: visibility === 'private' ? members.map(Number) : [],
      pinned_version: pinnedVersion === 'latest' ? null : Number(pinnedVersion),
    };

    if (install) {
      update.mutate({ id: install.public_id, input: base }, { onSuccess: () => onOpenChange(false) });
    } else if (template) {
      create.mutate(
        { ...base, template: template.id },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{install ? `Edit ${name}` : `Add ${name} to team`}</DialogTitle>
          <DialogDescription>
            {direction ? DIRECTION_LABELS[direction] : null}
            {install?.template_code ?? template?.code
              ? ` · ${install?.template_code ?? template?.code}`
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="agent-enabled">Enabled</Label>
              <p className="text-muted-foreground text-xs">
                Staff can resolve and run this agent via the dev13-agent skill.
              </p>
            </div>
            <Switch id="agent-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as AgentInstallStatus)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === 'draft' ? 'Draft' : s === 'active' ? 'Active' : 'Paused'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Pinned version</Label>
            <p className="text-muted-foreground text-xs">
              Pin the agent to a specific template version, or track the latest.
            </p>
            <Select
              value={pinnedVersion}
              onValueChange={setPinnedVersion}
              disabled={versions.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest (track newest)</SelectItem>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {v.version}
                    {v.model_hint ? ` · ${v.model_hint}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="agent-tools">Allowed tools</Label>
              <RecommendButton
                onClick={() => setAllowedTools((prev) => uniq([...prev, ...RECOMMENDED_TOOLS]))}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              The tools this agent may use. Empty = inherit the template default.
            </p>
            <MultiSelect
              id="agent-tools"
              value={allowedTools}
              onChange={setAllowedTools}
              options={TOOL_OPTIONS}
              placeholder="Inherit template default"
              searchPlaceholder="Search tools…"
              emptyText="No matching tool."
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="agent-kb-tags">KB tags</Label>
              <RecommendButton
                onClick={() =>
                  setKbTags((prev) =>
                    uniq([...prev, ...(direction ? (RECOMMENDED_KB_TAGS[direction] ?? []) : [])]),
                  )
                }
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Restrict the agent&apos;s retrieval to knowledge-base chunks with these tags.
            </p>
            <MultiSelect
              id="agent-kb-tags"
              value={kbTags}
              onChange={setKbTags}
              options={kbTagOptions}
              creatable
              placeholder="All knowledge (no tag filter)"
              searchPlaceholder="Search or add a tag…"
              emptyText="No tags yet — type to add one."
            />
          </div>

          <div className="space-y-1.5">
            <Label>Visibility</Label>
            <div className="inline-flex rounded-md border p-0.5" role="group" aria-label="Visibility">
              {(['public', 'private'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={visibility === v}
                  onClick={() => setVisibility(v)}
                  className={cn(
                    'cursor-pointer rounded px-3 py-1 text-sm transition-colors',
                    visibility === v
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {v === 'public' ? 'Public' : 'Private'}
                </button>
              ))}
            </div>
            <p className="text-muted-foreground text-xs">
              {visibility === 'public'
                ? 'Everyone on your team can see and run this agent.'
                : 'Only the members you choose can see and run it.'}
            </p>
            {visibility === 'private' ? (
              <MultiSelect
                value={members}
                onChange={setMembers}
                options={memberOptions}
                placeholder="Select members…"
                searchPlaceholder="Search people…"
                emptyText="No members found."
              />
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending}>
            {install ? 'Save' : 'Add to team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
